import { isAgentOnlyWidget, isAgentReportColumn } from 'constants/agentReporting';
import type { Column } from 'constants/filters';
import {
  COLUMN_BILLED,
  COLUMN_NOTES,
  COLUMN_PROJECT,
  COLUMN_TAGS,
  ITEMS_WITH_NO_TABLE_COLUMN,
} from 'constants/filters';
import type { CSSProperties } from 'react';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { fetchReportData } from 'redux/modules/superreports.actions';
import type { GenericWidgetRow, WidgetReference } from 'redux/modules/superreports.types';
import { collapseRowOnWidget, updateWidgetColumns } from 'redux/modules/superreports.types';
import type { ThunkDispatch } from 'types/redux';
import { TableWidgetPlaceholderBlock } from './blocks/TableWidgetPlaceholderBlock';

export interface TableColumnChangeParams {
  reference: string | number;
  order: 'asc' | 'desc';
  sort_by: string;
}

export const TABLE_WIDGET_INDENT_STEP = 16;

export const useHandleChangeColumns = (
  columns: Array<string>,
  reference: WidgetReference,
  expandedRowIds: number[] = []
) => {
  const dispatch = useDispatch<ThunkDispatch>();
  return useCallback(
    (column: string, checked: boolean) => {
      let updatedColumns = columns.filter(c => c !== column);
      if (checked) {
        updatedColumns = updatedColumns.concat([column]);
      }
      dispatch(
        updateWidgetColumns({
          names: updatedColumns,
          reference,
        })
      );
      if (isAgentOnlyWidget(columns) !== isAgentOnlyWidget(updatedColumns)) {
        expandedRowIds.forEach(rowId =>
          dispatch(collapseRowOnWidget({ widgetReference: reference, rowId }))
        );
      }
      if (columns.some(isAgentReportColumn) !== updatedColumns.some(isAgentReportColumn)) {
        dispatch(fetchReportData(true));
      }
    },
    [columns, dispatch, reference, expandedRowIds]
  );
};

export const filterVisibleColumns = (columns: Array<string>, availableColumns: Array<string>) =>
  (columns as Array<Column>).filter(
    column => availableColumns.includes(column) && !ITEMS_WITH_NO_TABLE_COLUMN.includes(column)
  );

/**
 * There are discrepancies in how currency formatting is done on the backend and frontend, so this
 * is a kitchen sink-ish way of determining if a formatted money value is zero.
 *
 * @param money The formatted money value. `undefined` values count as zero.
 */
export const isFormattedMoneyZero = (money: string | null | undefined) => {
  if (money == null) return true;
  const trimmedMoney = money.replace(/[,.]/g, '_').replace(/[^\d_]/g, '');
  return trimmedMoney === '0' || trimmedMoney === '0_00';
};

export const replaceZeroMoney = (money: string | null | undefined) =>
  isFormattedMoneyZero(money) ? <TableWidgetPlaceholderBlock /> : money;

export const getDepthStyle = (depth = 0, interactive?: boolean): CSSProperties => ({
  paddingLeft: TABLE_WIDGET_INDENT_STEP * (depth + (depth > 0 && !interactive ? 1 : 0)),
});

export const hasHourDetails = (hour: GenericWidgetRow, columns: Array<Column>) => {
  // This is intentionally flipped, as it will be displayed as a separate column if included
  const hasProject = !columns.includes(COLUMN_PROJECT) && !!hour.project;
  const hasTags = columns.includes(COLUMN_TAGS) && !!hour.labels?.length;
  const hasNote = columns.includes(COLUMN_NOTES) && !!hour.description;
  const hasBilledState = columns.includes(COLUMN_BILLED) && !!hour.isBilled;
  const hasInvoicedState = columns.includes(COLUMN_BILLED) && !!hour.isInvoiced;
  const hasLockedState = columns.includes(COLUMN_BILLED) && !!hour.isLocked;
  const hasDayLockedState = columns.includes(COLUMN_BILLED) && !!hour.isDayLocked;
  const hasAny =
    hasProject ||
    hasTags ||
    hasNote ||
    hasBilledState ||
    hasInvoicedState ||
    hasLockedState ||
    hasDayLockedState;

  return {
    hasAny,
    hasProject,
    hasTags,
    hasNote,
    hasBilledState,
    hasInvoicedState,
    hasLockedState,
    hasDayLockedState,
  };
};
